import { Injectable } from "@angular/core";
import { Platform } from '@ionic/angular';
import { File as NativeFile } from '@ionic-native/file/ngx';
import { FileOpener } from '@ionic-native/file-opener/ngx';
import * as signalR from "@microsoft/signalr";
import { nextTick } from "process";
import { BehaviorSubject, EMPTY, Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppService } from "./app.service";
import { LangsRepository } from "./langs.repository";
import { LeadClientRepositoryBUT } from "./lead.clients.repository.but";
import { LogService } from "./log.service";
import { NotificationService } from "./notification.service";
import { WhatsAppChatType } from "../Enums/whatsappChatType.enum";
import { ILang } from "../model/lang.interface";
import { ChatMessageType } from "../model/leadClients/lead.client.whatsapp.chatmessagetype";
import { LeadClientWhatsappReceivedMessage } from "../model/leadClients/lead.client.whatsapp.receivedmessage";
import { LeadClientWhatsappMessage } from "../model/leadClients/lead.client.whatsapp.message";
import { PoviderType } from "../model/leadClients/lead.client.whatsapp.providertype";
import { User } from "../model/user.model";
import { Result } from "../model/result";

// WhatsApp refuses larger media: image 5 MB, video and audio 16 MB. A document is held to what the
// server takes per request, 64,000,000 bytes for all files together (fixdigital.co.il#4529), less
// room for the multipart headers. Decimal MB, as phones show sizes.
const MAX_FILE_SIZE_MB: { [fileType: string]: number } = { image: 5, video: 16, audio: 16, file: 63 };
const MAX_REQUEST_SIZE_MB = 63;
const BYTES_IN_MB = 1000 * 1000;

// What the chats let a user attach. The file field lists the MIME types too: Android's picker matches `accept`
// by MIME type only and greys out every file when it gets bare extensions (#361); browsers go by the extensions.
// .jfif and .jpe are JPEG too: pickers offer them under image/jpeg, and the server takes them (fixdigital.co.il#4663).
export const ALLOWED_UPLOAD_EXTENSIONS = [
	".jpg", ".png", ".jpeg", ".jfif", ".jpe", ".mp4", ".3gpp", ".aac", ".amr", ".mp3", ".opus", ".pdf", ".doc", ".docx", ".ppt", ".pptx", ".xls", ".xlsx"
];
export const UPLOAD_FILE_ACCEPT = ALLOWED_UPLOAD_EXTENSIONS.concat([
	"image/jpeg", "image/png", "video/mp4", "video/3gpp", "audio/3gpp", "audio/aac", "audio/amr", "audio/mpeg", "audio/ogg",
	"application/pdf", "application/msword", "application/vnd.ms-powerpoint", "application/vnd.ms-excel",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.openxmlformats-officedocument.presentationml.presentation",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
]).join(",");

@Injectable()
export class WhatsappService {
    private isConnected = false;
    private signalRConnectionSubject = new BehaviorSubject<signalR.HubConnection>(null);
    public signalRConnection$ = this.signalRConnectionSubject.asObservable();
    public signalRConnection: signalR.HubConnection;
    public signalRConnectionSubscription: Subscription;
	private startConnectionPromise: Promise<void> | null = null;
	private subscribedUserId: number | null = null;
    private receivedMessageSubject = new BehaviorSubject<LeadClientWhatsappReceivedMessage>(null);
    public receivedMessage$ = this.receivedMessageSubject.asObservable();
    public receivedMessage: LeadClientWhatsappReceivedMessage;
    public receivedMessageSubscription: Subscription;
    private signalRConnectionCloseRequested = false;
    private token = "";
    private chatType = WhatsAppChatType.Client;
    private readonly messageFileErrorPlaceholder = "__messagefile_error__";
    private readonly signalRRetryDelayMs = 5000;

    constructor(
        protected langsRepository: LangsRepository,
        protected leadClientRepository: LeadClientRepositoryBUT,
        private logService: LogService,
        private file: NativeFile,
        private fileOpener: FileOpener,
        public readonly notificationService: NotificationService,
        private platform: Platform,
        private appService: AppService
    ) { }

    get currentLang(): ILang { return this.langsRepository.current };

    setSignalRConnection(signalRConnection: signalR.HubConnection) {
        this.signalRConnectionSubject.next(signalRConnection);
    }

    setReceivedMessage(receivedMessage: LeadClientWhatsappReceivedMessage) {
        if (receivedMessage) {
            // signalR sends null text for messages without text content (MessageContentType "none")
            receivedMessage.message = receivedMessage.message || "";
        }
        this.receivedMessageSubject.next(receivedMessage);
    }

    public setToken(token: string): void {
        this.token = token;
    }

    public setWhatsAppChatType(chatType: WhatsAppChatType): void {
        this.chatType = chatType;
    }

    public async startConnection(userId: number) {
		if (!this.signalRConnection) {
			return;
        }

		if (this.startConnectionPromise) {
			await this.startConnectionPromise;
			return;
		}

		const state = this.signalRConnection.state;
		if (state === signalR.HubConnectionState.Connected) {
			this.isConnected = true;
			return;
		}

		if (state !== signalR.HubConnectionState.Disconnected) {
			return;
		}

		console.log('startConnection', userId);
		this.signalRConnectionCloseRequested = false;
		const connection = this.signalRConnection;
		this.startConnectionPromise = this.signalRConnection.start()
			.then(async () => {
				if (this.signalRConnection) {
					await this.signalRConnection.invoke('ConnectUser', userId);
					try {
						if (sessionStorage.getItem('crm-app-impersonation-session')) {
							await this.signalRConnection.invoke('MarkImpersonationSession');
							sessionStorage.removeItem('crm-app-impersonation-session');
						}
					} catch (_) { }
					this.isConnected = true;
				}
			})
			.catch(err => {
				console.error('start signalRConnection error', err)
				// Automatic reconnect covers only a connection that once started, so a failed start() -
				// the restart in onclose included - is retried here (#350).
				setTimeout(() => {
					if (this.signalRConnection === connection && !this.signalRConnectionCloseRequested) {
						this.startConnection(userId);
					}
				}, this.signalRRetryDelayMs);
			})
			.finally(() => {
				this.startConnectionPromise = null;
			});

		await this.startConnectionPromise;
    };

    public async subscribe(user: User): Promise<void> {
		if (!user) {
			return;
		}

        await this.platform.ready();

		if (this.signalRConnection &&
			this.subscribedUserId === user.id &&
			this.signalRConnection.state !== signalR.HubConnectionState.Disconnected) {
			return;
		}

		if (this.signalRConnection &&
			this.subscribedUserId === user.id &&
			this.signalRConnection.state === signalR.HubConnectionState.Disconnected) {
			await this.startConnection(user.id);
			return;
		}

		if (this.signalRConnectionSubscription) {
			this.signalRConnectionSubscription.unsubscribe();
			this.signalRConnectionSubscription = null;
		}

		if (this.signalRConnection && this.subscribedUserId !== user.id) {
			const connectionToClose = this.signalRConnection;
			const pendingStart = this.startConnectionPromise;
			this.signalRConnectionCloseRequested = true;
			this.signalRConnection = null;
			this.setSignalRConnection(null);
			this.isConnected = false;

			this.closeConnection(connectionToClose, pendingStart, null);
		}

		this.subscribedUserId = user.id;

        this.signalRConnectionSubscription = this.signalRConnection$.subscribe(async (con: signalR.HubConnection) => {
            if (con !== null && !this.signalRConnection) {
                this.signalRConnection = con;
                await this.startConnection(user.id);
            }
        });

        const connectionOptions = { 
            transport: signalR.HttpTransportType.WebSockets, 
            skipNegotiation: true 
        };
        let connection = new signalR.HubConnectionBuilder()
            .withUrl(`${environment.baseFixUrl}chatHub`, connectionOptions)
            .configureLogging(signalR.LogLevel.Information)
            // The default policy gives up after ~42 s (0, 2, 10, 30 s), which left the app deaf until
            // restarted after a longer network drop. Retry for as long as it takes: an attempt without
            // network fails at once, so trying every few seconds costs nothing (#350).
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retry: signalR.RetryContext) => retry.previousRetryCount === 0 ? 0 : this.signalRRetryDelayMs
            })
            .build();
        
        // Handle ForceLogout notification from backend
        connection.on("ForceLogout", (data: any) => {
            console.log("Received ForceLogout notification", data);
            window.dispatchEvent(new CustomEvent('force-logout', { detail: data }));
        });

        connection.on("CompanySwitched", (companyId: number, jwt?: string, isImpersonation?: boolean) => {
            if (jwt) {
                const isInIframe = (parent !== window);
                const hostname = isInIframe ? document.referrer : window.location.hostname;
                const authKey = `fxa-${hostname}`;
                try {
                    const raw = localStorage.getItem(authKey);
                    if (raw) {
                        const data = JSON.parse(raw);
                        data.jwt = jwt;
                        localStorage.setItem(authKey, JSON.stringify(data));
                    }
                } catch (_) { }
            }
            if (isImpersonation) {
                try { sessionStorage.setItem('crm-app-impersonation-session', '1'); } catch (_) { }
            }
            window.location.reload();
        });
        
        connection.onclose(async (error?: Error) => {
            if (!this.signalRConnectionCloseRequested) {
                await this.startConnection(user.id);
            }
            if (error) {
                console.error("error in connection.onclose", error);
            }
        });

        // A reconnect comes back with a new connection id, and the hub pushes only to ids that
        // ran ConnectUser - without this the app stops getting messages after a network drop (#350).
        connection.onreconnected(() => {
            connection.invoke('ConnectUser', user.id)
                .catch(err => console.error('ConnectUser after reconnect error', err));
        });
        this.setSignalRConnection(connection);
    }

    public unsubscribe(user: User): void {
		const connectionToClose = this.signalRConnection;
		const pendingStart = this.startConnectionPromise;

		this.signalRConnectionCloseRequested = true;
		this.signalRConnection = null;
		this.setSignalRConnection(null);
		this.isConnected = false;
		this.subscribedUserId = null;

		if (this.signalRConnectionSubscription) {
			this.signalRConnectionSubscription.unsubscribe();
			this.signalRConnectionSubscription = null;
		}

		this.setReceivedMessage(null);
		this.receivedMessage = null;
		if (this.receivedMessageSubscription) {
			this.receivedMessageSubscription.unsubscribe();
			this.receivedMessageSubscription = null;
		}

		if (connectionToClose) {
			this.closeConnection(connectionToClose, pendingStart, user.id);
		}
    }

	// stop() during an unfinished handshake makes signalR reject the pending start(); zone.js reports
	// that rejection as unhandled whatever .catch() the caller attaches, because HubConnection returns
	// `Promise.reject()` out of an async method. So let the in-flight start settle before stopping.
	private async closeConnection(
		connection: signalR.HubConnection,
		pendingStart: Promise<void> | null,
		disconnectUserId: number | null
	): Promise<void> {
		if (pendingStart) {
			await pendingStart;
		}

		if (disconnectUserId !== null && connection.state === signalR.HubConnectionState.Connected) {
			try {
				await connection.invoke('DisconnectUser', disconnectUserId);
			} catch (err) {
				console.error('DisconnectUser signalRConnection error', err);
			}
		}

		try {
			await connection.stop();
		} catch (err) {
			console.error('stop signalRConnection error', err);
		}
	}

    public formatWhatsappMessage(msg: LeadClientWhatsappMessage) {
		if (!msg.message) {
			return "";
		}
		msg.message = msg.message.replace(/\n/g, "<br />");

		const urlRegex = /\b((https?:\/\/\S+(?:png|jpe?g|gif)\S*)|(https?:\/\/api\-eu\.nexmo\.com\/v3\/media\/\S+))\b/gi;
		const fileRegex = /\b(leadClient\/getMessageFile\/(\w+)\/)(.+?\.([a-zA-Z]\w+\b|$))/i;
        const fileNameRegex = /[^\/]+$/;

		let datePast = new Date();
		const createDate = new Date(msg.createDate);
		if (urlRegex.test(msg.message)) {
			datePast.setDate(datePast.getDate() - 90);
			if (createDate <= datePast) {
				return msg.message.replace(urlRegex, `<b>${this.currentLang.words["image-deleted"]}</b>`);
			}
			return msg.message
                // replace uploaded images to alias with https (instead of http)
                .replace("http://prod2-clp-fixdigital.s3-website-eu-west-1.amazonaws.com", "https://lpc.fixdigital.co.il")
                .replace(urlRegex, `<a href='$1' target='_blank'><img src='$1' alt='image' /></a>`);
		}
		if (fileRegex.test(msg.message)) {
			datePast.setDate(datePast.getDate() - 90);

			if (createDate <= datePast || msg.message.includes('/deleted/')) {
				return msg.message.replace(fileRegex, `<b>${this.currentLang.words["image-deleted"]}</b>`);
			}
			if (!msg.fileUrl) {
				let filename = msg.filename ? encodeURIComponent(msg.filename) : encodeURIComponent(msg.message.match(fileRegex)[3]);
				let url = msg.message.match(fileRegex)[1] + filename;
				var getMessageFileMethodPromise = this.chatType === WhatsAppChatType.Client
						? this.leadClientRepository.getMessageFile(url)
						: this.leadClientRepository.getMessageFileWithCrmToken(this.token, url);
				getMessageFileMethodPromise.then(blob => {
					msg.file = blob;
					let fileUrl = window.URL.createObjectURL(blob);
					msg.fileUrl = fileUrl;
				})
				.catch(reason => {
					console.log('Error in getMessageFileMethodPromise: ', reason, url, msg.messageContentType, msg.message);
					// Stop the retry loop and show the original message text instead.
					msg.fileUrl = this.messageFileErrorPlaceholder;
					msg.imgSrc = null;
				});

				msg.fileUrl = '/loading.svg';
			}

			// If loading failed, render the original message (including the getMessageFile/... text)
			// and do not try to request again.
			if (msg.fileUrl === this.messageFileErrorPlaceholder) {
				msg.imgSrc = null;
				// Wrap with a light red background to make the error visible to the user.
				return `<div style="background-color:#ffe6e6; padding:6px 8px; border-radius:6px; display:inline-block;">${msg.message}</div>`;
			}

			if (msg.messageContentType == "video") {
				return msg.message.replace(fileRegex, `<video controls><source src='${msg.fileUrl}'> </video>`);
			}

			if (msg.messageContentType == "audio") {
				return msg.message.replace(fileRegex, `<audio controls><source src='${msg.fileUrl}'> </audio>`);
			}

			if (msg.messageContentType == "file") {
				fileRegex.lastIndex = 0;
				const match = fileRegex.exec(msg.message);
                let filename = "";
                if (msg.filename) {
                    filename = msg.filename;
                } else {
                    const matchFilename = fileNameRegex.exec(match[0]);
                    filename = match ? matchFilename[0] : "unknown";
                }
				return msg.message.replace(fileRegex, this.createFileMessageHtml(filename, msg.fileUrl));
			}

			nextTick(() => {
				msg.imgSrc = msg.fileUrl;
			});
			return msg.message.replace(fileRegex, "");
		}

		if (msg.adPreview) {
			return `${msg.message} ${msg.adPreview}`;
		}

        const anchorRegex = /(<a[^>]*>)([^<]+)(<\/a>)/gi;
        if (msg.messageType === ChatMessageType.Template && msg.filename && anchorRegex.test(msg.message)) {
            return msg.message.replace(anchorRegex, `$1${msg.filename}$3`);
        }

		return msg.message;
	}

	public async openImage(msg: LeadClientWhatsappMessage): Promise<void> {
		if (!this.platform.is("cordova")) {
			window.open(msg.imgSrc, "_blank");
			return;
		}
		if (!msg.file) {
			return;
		}
		// Without its Cordova plugin an ionic-native call returns undefined instead of a promise (#352)
		if (!NativeFile.installed() || !FileOpener.installed()) {
			this.appService.logToSentry("Cannot open a WhatsApp image: cordova-plugin-file or file-opener2 is not in the build");
			return;
		}
		try {
			const image = await this.file.writeFile(this.file.dataDirectory, "tempimage.jpeg", msg.file, { replace: true });
			await this.fileOpener.open(image.nativeURL, "image/jpeg");
		} catch (err) {
			this.logService.add(`error opening image: ${JSON.stringify(err)}`);
		}
	}

    private createFileMessageHtml(filename: string, fileUrl: string): string {
		return "<div class='file'>" +
			"<span class='file-icon'></span>" +
			"<div class='meta'>" +
			`<div class='title'>${filename}</div>` +
			`<a target="_blank" href='${fileUrl}' class="download" download=${filename}>Download</a>` +
			"</div>" +
			"</div>"
	}

	// region Sending messages
	private mediaTypes = ['video', 'audio', 'image'];

	private mapFileType(file: File): string {
		const fileType = file.type;
		for (const category of this.mediaTypes) {
			if (fileType.startsWith(`${category}/`)) {
				return category;
			}
		}
		return "file";
	}

	/** The notice for files WhatsApp or the server would not take, or null when all of them fit. */
	private getOversizeNotice(files: File[] | null): string | null {
		const selected: File[] = Array.from(files || []);
		const tooLarge = selected.find(file => file.size > MAX_FILE_SIZE_MB[this.mapFileType(file)] * BYTES_IN_MB);
		if (tooLarge) {
			return this.currentLang.words["whatsapp-file-too-large"]
				.replace("{file}", tooLarge.name)
				.replace("{size}", String(MAX_FILE_SIZE_MB[this.mapFileType(tooLarge)]));
		}
		const totalSize = selected.reduce((sum, file) => sum + file.size, 0);
		if (totalSize > MAX_REQUEST_SIZE_MB * BYTES_IN_MB) {
			return this.currentLang.words["whatsapp-files-too-large"].replace("{size}", String(MAX_REQUEST_SIZE_MB));
		}
		return null;
	}

	public sendReplyMessage(params: {
		token?: string;
		leadClientId: number;
		userId: number;
		messageText: string;
		filesToUpload?: File[] | null;
		selectedPhone?: string;
		senderOrigin?: string;
		senderDestination?: string;
	}): Observable<LeadClientWhatsappMessage[]> {
		const oversizeNotice = this.getOversizeNotice(params.filesToUpload);
		if (oversizeNotice) {
			// Shown here, not by the caller's showError: that adds a fault number nothing records for a soft notice.
			this.appService.titleOfTooltipPopup = this.currentLang.words["error-short"];
			this.appService.textOfTooltipPopup = oversizeNotice;
			this.appService.tooltipPopupActive = true;
			return EMPTY;
		}

		const request$ = params.token
			? this.leadClientRepository.sendReplyWhatsappMessageWithCrmToken(
				params.token,
				params.leadClientId,
				params.messageText,
				params.userId,
				params.filesToUpload,
				params.selectedPhone
			)
			: this.leadClientRepository.sendReplyWhatsappMessage(
				params.leadClientId,
				params.messageText,
				params.userId,
				params.filesToUpload,
				params.selectedPhone
			);

		return request$.pipe(
			map((response: any) => {
				const result: Result = response.data;
				if (!result || !result.success) {
					throw new Error((result && result.message) || "Send message failed");
				}

				const senderOrigin = params.senderOrigin || "";
				const senderDestination = params.senderDestination || "";
				const hasFiles = !!(params.filesToUpload && params.filesToUpload.length > 0);

				if (hasFiles) {
					const uploadData: any = result.data || {};
					const fileUrls: string[] = Array.isArray(uploadData.filesUrl) ? uploadData.filesUrl : [];
					const contentMessageType = params.filesToUpload.length === 1 ? this.mapFileType(params.filesToUpload[0]) : "file";

					return fileUrls.map((fileUrl: string) =>
						new LeadClientWhatsappMessage(
							0, PoviderType.Whatsapp, new Date(), params.leadClientId, ChatMessageType.Reply, fileUrl, senderOrigin, senderDestination, params.userId, contentMessageType
						)
					);
				}

				return [
					new LeadClientWhatsappMessage(
						0, PoviderType.Whatsapp, new Date(), params.leadClientId, ChatMessageType.Reply, params.messageText, senderOrigin, senderDestination, params.userId, "text"
					)
				];
			})
		);
	}
	// endregion

	// region UI helpers - textarea Enter/newline handling
	/**
	 * Compute handling for Enter in a textarea.
	 * - Plain Enter => send
	 * - Any modifier (Ctrl/Shift/Alt/Meta) + Enter => insert newline at caret
	 */
	public handleTextareaEnterKey(
		event: KeyboardEvent,
		currentText: string
	): { updatedText: string | null; newCaret: number | null; shouldSend: boolean; preventDefault: boolean } {
		if (event.key !== "Enter") {
			return { updatedText: null, newCaret: null, shouldSend: false, preventDefault: false };
		}
		const textarea = event.target as HTMLTextAreaElement;
		if (event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) {
			// Insert newline at caret (replace selection if any)
			const selectionStart = textarea.selectionStart || 0;
			const selectionEnd = textarea.selectionEnd || 0;
			const before = currentText.slice(0, selectionStart);
			const after = currentText.slice(selectionEnd);
			const updatedText = `${before}\n${after}`;
			return {
				updatedText,
				newCaret: selectionStart + 1,
				shouldSend: false,
				preventDefault: true
			};
		}
		// Plain Enter => send
		return { updatedText: null, newCaret: null, shouldSend: true, preventDefault: true };
	}
	// endregion

	public getReferralUrl(referralData: string): string | null {
		if (!referralData) {
			return null;
		}
		
		try {
			const referral = JSON.parse(referralData);
			// WhatsApp Referral может содержать source_url или sourceUrl
			return referral.source_url || referral.sourceUrl || null;
		} catch (e) {
			console.warn('Failed to parse referralData:', e);
			return null;
		}
	}
}

